const HEADING = /^(#{1,6})\s+(.+)$/;
const BULLET = /^\s*[-*•]\s+(.+)$/;
const NUMBER = /^\s*\d+[.)]\s+(.+)$/;
function parseBlocks(markdown) {
    const blocks = [];
    let paragraph = [];
    const flushParagraph = () => {
        if (paragraph.length)
            blocks.push({ kind: 'paragraph', text: paragraph.join(' ') });
        paragraph = [];
    };
    for (const rawLine of markdown.replace(/\r\n?/g, '\n').split('\n')) {
        const line = rawLine.trimEnd();
        if (!line.trim()) {
            flushParagraph();
            continue;
        }
        const heading = HEADING.exec(line);
        if (heading) {
            flushParagraph();
            // `#` is the page title, which the page itself owns, so it becomes the first level below.
            const level = Math.min(Math.max(heading[1].length, 2), 4);
            blocks.push({ kind: 'heading', level, text: heading[2].trim() });
            continue;
        }
        const bullet = BULLET.exec(line);
        const number = bullet ? null : NUMBER.exec(line);
        const match = bullet ?? number;
        if (match) {
            flushParagraph();
            const kind = bullet ? 'bullets' : 'numbers';
            const last = blocks[blocks.length - 1];
            if (last && last.kind === kind)
                last.items.push(match[1].trim());
            else
                blocks.push({ kind, items: [match[1].trim()] });
            continue;
        }
        paragraph.push(line.trim());
    }
    flushParagraph();
    return blocks;
}
/** `**bold**` and `*italic*`; everything else, including stray asterisks, is text. */
function inline(text) {
    const nodes = [];
    const pattern = /\*\*([^*]+)\*\*|\*([^*\s][^*]*)\*/g;
    let cursor = 0;
    const push = (value, mark) => {
        if (!value)
            return;
        nodes.push({ type: 'text', text: value, ...(mark ? { marks: [mark] } : {}) });
    };
    for (const match of text.matchAll(pattern)) {
        push(text.slice(cursor, match.index));
        if (match[1] !== undefined)
            push(match[1], { type: 'bold' });
        else
            push(match[2], { type: 'italic' });
        cursor = match.index + match[0].length;
    }
    push(text.slice(cursor));
    return nodes;
}
const paragraph = (text) => ({ type: 'paragraph', content: inline(text) });
export function markdownToContentNodes(markdown) {
    return parseBlocks(markdown).map((block) => {
        switch (block.kind) {
            case 'heading':
                return { type: 'heading', attrs: { level: block.level }, content: inline(block.text) };
            case 'bullets':
            case 'numbers':
                return {
                    type: block.kind === 'bullets' ? 'bulletList' : 'orderedList',
                    content: block.items.map((item) => ({ type: 'listItem', content: [paragraph(item)] })),
                };
            default:
                return paragraph(block.text);
        }
    });
}
export function markdownToContentDoc(markdown) {
    return { type: 'doc', content: markdownToContentNodes(markdown) };
}
/**
 * The selection or the document as the same markdown, so a model can be
 * handed what is on the page. Marks other than bold and italic, images and
 * tables are flattened to their text: the model is only editing words.
 */
export function contentNodesToMarkdown(nodes) {
    const runs = (content = []) => content
        .map((node) => {
        if (node.type === 'hardBreak')
            return ' ';
        if (node.type !== 'text')
            return runs(node.content);
        const marks = node.marks ?? [];
        const text = node.text ?? '';
        if (marks.some((mark) => mark.type === 'bold'))
            return `**${text}**`;
        if (marks.some((mark) => mark.type === 'italic'))
            return `*${text}*`;
        return text;
    })
        .join('');
    const lines = [];
    for (const node of nodes) {
        switch (node.type) {
            case 'heading':
                lines.push(`${'#'.repeat(node.attrs?.level ?? 2)} ${runs(node.content)}`);
                break;
            case 'bulletList':
                (node.content ?? []).forEach((item) => lines.push(`- ${runs(item.content?.[0]?.content)}`));
                break;
            case 'orderedList':
                (node.content ?? []).forEach((item, index) => lines.push(`${index + 1}. ${runs(item.content?.[0]?.content)}`));
                break;
            case 'paragraph':
            case 'blockquote':
                lines.push(node.type === 'blockquote' ? (node.content ?? []).map((p) => runs(p.content)).join(' ') : runs(node.content));
                break;
            default:
                break;
        }
        lines.push('');
    }
    return lines.join('\n').trim();
}
